import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import "dayjs/locale/ru";

// Подключаем плагины для работы с часовыми поясами
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);
dayjs.locale("ru");

// Устанавливаем часовой пояс по умолчанию (Москва)
const DEFAULT_TIMEZONE = "Europe/Moscow";

/**
 * Парсинг UTC даты и конвертация в локальное время
 */
export const parseDate = (date: string | Date | null | undefined): dayjs.Dayjs | null => {
  if (!date) return null;
  // Парсим как UTC и конвертируем в локальное время
  return dayjs.utc(date).local();
};

/**
 * Форматирование даты в стандартный формат (с конвертацией в локальное время)
 */
export const formatDate = (date: string | Date | null | undefined, format: string = "YYYY-MM-DD HH:mm"): string => {
  if (!date) return "";
  // Парсим как UTC и конвертируем в локальное время
  return dayjs.utc(date).local().format(format);
};

/**
 * Форматирование даты в относительный формат ("2 часа назад")
 */
export const formatRelativeDate = (date: string | Date | null | undefined): string => {
  if (!date) return "";
  return dayjs.utc(date).local().fromNow();
};

/**
 * Конфигурация статусов для отзывов
 */
export const REVIEW_STATUSES: Record<string, { label: string; color: string }> = {
  new: { label: "Новый", color: "blue" },
  processing: { label: "Обработка", color: "processing" },
  ready: { label: "Готов", color: "green" },
  moderated: { label: "Проверен", color: "cyan" },
  published: { label: "Опубликован", color: "success" },
  rejected: { label: "Отклонен", color: "default" },
  manual_review: { label: "Ручная проверка", color: "red" },
};

/**
 * Конфигурация статусов для ответов
 * ВАЖНО: Это единственный источник истины для названий статусов!
 */
export const RESPONSE_STATUSES: Record<string, { label: string; color: string; icon?: string }> = {
  pending: { label: "Ожидает", color: "blue", icon: "⏳" },
  draft: { label: "Черновик", color: "orange", icon: "✏️" },
  approved: { label: "Утвержден", color: "cyan", icon: "✅" },
  published: { label: "Опубликован", color: "green", icon: "📤" },
  publish_failed: { label: "Ошибка", color: "red", icon: "❌" },
  // Legacy статусы (для обратной совместимости)
  ready: { label: "Готов", color: "blue" },
  rejected: { label: "Отклонен", color: "default" },
  failed: { label: "Ошибка публикации", color: "red" },
};

/**
 * Получить конфиг статуса
 */
export const getStatusConfig = (
  status: string,
  statusMap: Record<string, { label: string; color: string; icon?: string }>
) => {
  return statusMap[status] || { label: status, color: "default" };
};

/**
 * Форматирование источника
 */
export const SOURCE_CONFIG: Record<string, { label: string; color: string }> = {
  wildberries_cab1: { label: "WB", color: "purple" },
  wildberries_cab2: { label: "WB", color: "purple" },
  ozon: { label: "OZON", color: "blue" },
  excel_import: { label: "EXCEL", color: "green" },
};

export const formatSource = (source: string) => {
  if (source.startsWith('wildberries')) {
    return { label: "WB", color: "purple" };
  }
  return SOURCE_CONFIG[source] || { label: source, color: "default" };
};

/**
 * Форматирование приоритета правила маршрутизации
 */
export const getPriorityColor = (priority: number): string => {
  if (priority >= 90) return "red";
  if (priority >= 70) return "orange";
  if (priority >= 40) return "blue";
  return "default";
};

/**
 * Усечение текста с многоточием
 */
export const truncateText = (text: string | null | undefined, maxLength: number = 100): string => {
  if (!text) return "";
  return text.length > maxLength ? text.slice(0, maxLength) + "…" : text;
};
